import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AuthenticatedUser, OrganizationSummary } from '@stockpilot/shared';

const ACCESS_TOKEN_KEY = 'sp.accessToken';
const REFRESH_TOKEN_KEY = 'sp.refreshToken';

export interface AuthState {
  user: AuthenticatedUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  organizations: OrganizationSummary[];
  /** False until the first refresh attempt settles, so the UI can hold the shell. */
  initialized: boolean;
}

/**
 * The refresh token also lives in an httpOnly cookie set by the API. It is
 * mirrored here only so non-browser clients and hard reloads keep working; the
 * cookie is what the API actually prefers.
 */
function readStoredToken(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStoredToken(key: string, value: string | null): void {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    /* private browsing — the session simply will not survive a reload */
  }
}

const initialState: AuthState = {
  user: null,
  accessToken: readStoredToken(ACCESS_TOKEN_KEY),
  refreshToken: readStoredToken(REFRESH_TOKEN_KEY),
  organizations: [],
  initialized: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    credentialsReceived(
      state,
      action: PayloadAction<{
        user: AuthenticatedUser;
        accessToken: string;
        refreshToken: string;
        organizations?: OrganizationSummary[];
      }>,
    ) {
      state.user = action.payload.user;
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.organizations = action.payload.organizations ?? state.organizations;
      state.initialized = true;
      writeStoredToken(ACCESS_TOKEN_KEY, action.payload.accessToken);
      writeStoredToken(REFRESH_TOKEN_KEY, action.payload.refreshToken);
    },
    tokensRefreshed(
      state,
      action: PayloadAction<{ accessToken: string; refreshToken: string }>,
    ) {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      writeStoredToken(ACCESS_TOKEN_KEY, action.payload.accessToken);
      writeStoredToken(REFRESH_TOKEN_KEY, action.payload.refreshToken);
    },
    profileLoaded(
      state,
      action: PayloadAction<{ user: AuthenticatedUser; organizations: OrganizationSummary[] }>,
    ) {
      state.user = action.payload.user;
      state.organizations = action.payload.organizations;
      state.initialized = true;
    },
    initializationFinished(state) {
      state.initialized = true;
    },
    loggedOut(state) {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.organizations = [];
      state.initialized = true;
      writeStoredToken(ACCESS_TOKEN_KEY, null);
      writeStoredToken(REFRESH_TOKEN_KEY, null);
    },
  },
});

export const {
  credentialsReceived,
  tokensRefreshed,
  profileLoaded,
  initializationFinished,
  loggedOut,
} = authSlice.actions;

export default authSlice.reducer;
